// client/src/pages/admin/AdminLayout.js
import React, { useContext } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Container, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SideNav from '../../components/SideNav';
import { AuthContext } from '../../context/AuthContext';
import { useActingStore } from '../../context/ActingStoreContext';

const ADMIN_NAV_GROUPS = [
  {
    items: [
      { to: '/admin', label: 'Dashboard', icon: DashboardOutlinedIcon, end: true },
      { to: '/admin/reports', label: 'Reports', icon: AnalyticsOutlinedIcon },
    ],
  },
  {
    title: 'Sales',
    items: [
      { to: '/admin/orders', label: 'Orders', icon: ShoppingBagOutlinedIcon },
      { to: '/admin/return-requests', label: 'Return Requests', icon: AssignmentReturnOutlinedIcon },
      { to: '/admin/coupons', label: 'Coupons', icon: LocalOfferOutlinedIcon },
      { to: '/profile?view=messages', label: 'Customer Messages', icon: ChatBubbleOutlineIcon },
    ],
  },
  {
    title: 'Catalogue',
    items: [
      { to: '/admin/products', label: 'Products', icon: Inventory2OutlinedIcon },
      { to: '/admin/stock', label: 'Stock', icon: WarehouseOutlinedIcon },
      { to: '/admin/categories', label: 'Categories', icon: CategoryOutlinedIcon },
    ],
  },
  {
    title: 'Documents',
    items: [
      { to: '/admin/invoices', label: 'Invoices', icon: ReceiptLongOutlinedIcon },
      { to: '/admin/quotes', label: 'Quotes', icon: RequestQuoteOutlinedIcon },
      { to: '/admin/proformas', label: 'Proforma Invoices', icon: DescriptionOutlinedIcon },
    ],
  },
  {
    title: 'Store',
    items: [
      { to: '/admin/store', label: 'Manage Store', icon: StorefrontOutlinedIcon },
      { to: '/admin/users', label: 'Users', icon: PeopleOutlineIcon },
      { to: '/admin/feature-flags', label: 'Site Features', icon: ToggleOnOutlinedIcon },
    ],
  },
];

const AdminLayout = () => {
  const { user } = useContext(AuthContext);
  const { actingStoreId } = useActingStore();
  const { pathname } = useLocation();
  const isSuperAdmin = user?.role === 'super_admin';

  const groups = ADMIN_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      key: item.to,
      selected: item.end ? pathname === item.to : pathname.startsWith(item.to),
    })),
  }));

  return (
    <Container maxWidth={false} sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ display: 'grid', gap: { xs: 2, md: 4 }, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '230px minmax(0, 1fr)' }, alignItems: 'start' }}>
        <SideNav
          title="Admin Panel"
          groups={groups}
          footer={(
            <Button component={Link} to="/" size="small" startIcon={<ArrowBackIcon />} sx={{ ml: 0.5 }}>
              Back to store
            </Button>
          )}
        />
        <Paper sx={{ p: { xs: 2, md: 3.5 }, minHeight: 480, minWidth: 0 }}>
          {isSuperAdmin && !actingStoreId ? <Alert severity="warning">No store found.</Alert> : <Outlet />}
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminLayout;
